import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { detectUserRegion, DetectedRegion } from '../services/ipDetection';
import { trackGAEvent } from '../common/analytics';

interface ShippingRegionContextType {
	selectedRegion: string | null;
	updateSelectedRegion: (regionCode: string | null) => void;
	clearSelectedRegion: () => void;
	isLoading: boolean;
	detectedRegion: DetectedRegion | null;
	hasAutoDetected: boolean;
}

const ShippingRegionContext = createContext<ShippingRegionContextType | undefined>(undefined);

interface ShippingRegionProviderProps {
	children: ReactNode;
}

const SHIPPING_REGION_STORAGE_KEY = 'selectedShippingRegion';

export const ShippingRegionProvider: React.FC<ShippingRegionProviderProps> = ({ children }) => {
	const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasAutoDetected, setHasAutoDetected] = useState(false);
	const [detectedRegion, setDetectedRegion] = useState<DetectedRegion | null>(null);
	const [isDetecting, setIsDetecting] = useState(false);

	const detectRegion = async () => {
		if (hasAutoDetected || isDetecting) {
			return;
		}
		setIsDetecting(true);
		try {
			const detected = await detectUserRegion();
			if (detected) {
				setDetectedRegion(detected);
			}
			// Always mark as auto-detected, even if detection failed (returned null)
			setHasAutoDetected(true);
		} catch (error) {
			console.warn('Failed to detect region:', error);
			// Mark as auto-detected even on error to prevent infinite retries
			setHasAutoDetected(true);
		} finally {
			setIsDetecting(false);
		}
	};

	useEffect(() => {
		detectRegion();
		const savedRegion = localStorage.getItem(SHIPPING_REGION_STORAGE_KEY);
		if (savedRegion) {
			setSelectedRegion(savedRegion);
		}
		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (detectedRegion && !selectedRegion) {
			setSelectedRegion(detectedRegion.countryCode);
			localStorage.setItem(SHIPPING_REGION_STORAGE_KEY, detectedRegion.countryCode);
			trackGAEvent({
				category: 'UI',
				action: `RegionDetected: ${detectedRegion.countryCode}`,
			});
		}
	}, [detectedRegion, selectedRegion]);

	// Save to localStorage whenever region changes
	const updateSelectedRegion = useCallback((regionCode: string | null) => {
		setSelectedRegion(regionCode);
		if (regionCode) {
			localStorage.setItem(SHIPPING_REGION_STORAGE_KEY, regionCode);
		} else {
			localStorage.removeItem(SHIPPING_REGION_STORAGE_KEY);
		}
	}, []);

	// Clear selected region
	const clearSelectedRegion = useCallback(() => {
		setSelectedRegion(null);
		localStorage.removeItem(SHIPPING_REGION_STORAGE_KEY);
		setHasAutoDetected(false);
		setDetectedRegion(null);
	}, []);

	const contextValue = {
		selectedRegion,
		updateSelectedRegion,
		clearSelectedRegion,
		isLoading: isLoading || isDetecting,
		detectedRegion,
		hasAutoDetected
	};

	return (
		<ShippingRegionContext.Provider value={contextValue}>
			{children}
		</ShippingRegionContext.Provider>
	);
};

export const useShippingRegionContext = () => {
	const context = useContext(ShippingRegionContext);
	if (context === undefined) {
		throw new Error('useShippingRegionContext must be used within a ShippingRegionProvider');
	}
	return context;
}; 